import { Controller, useFormContext, useWatch } from 'react-hook-form';
import dynamic from 'next/dynamic';

import {
  FEMALE_TITLES,
  type Form,
} from '../../../queries/schema/carFinance.schema';
import { useModal } from '../shared/FinanceModal/FinanceModal.hook';

import { MobilePhoneNumberInput } from '../shared/MobilePhoneNumberInput/MobilePhoneNumberInput';
import { FinanceTextField } from '../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { FinanceSelect } from '../../../financeProviders/shared/FinanceSelect';
import { FinanceDatepickerField } from '../../../financeProviders/shared/FinanceDatepicker';
import { TextWithHelpIcon } from '../shared/TextWithHelpIcon/TextWithHelpIcon';

import * as Styled from './PersonalDetails.styled';

import {
  countryOptions,
  genderOptions,
  maritalStatusOptions,
  numberOfDependantsOptions,
  titleOptions,
} from './constants';

import { type PPSNumberInfoModalProps } from './components/PPSNumberInfoModal/PPSNumberInfoModal';

const DynamicPPSNumberInfoModal = dynamic<PPSNumberInfoModalProps>(
  import('./components/PPSNumberInfoModal/PPSNumberInfoModal').then(
    (mod) => mod.PPSNumberInfoModal,
  ),
  { ssr: false },
);

/**
 * Contact fields (title, name, phone, email) for step 1 "Your details" sections.
 */
const ContactFields = () => {
  const { control } = useFormContext<Form>();
  return (
    <Styled.FormInputsContainer>
      <Styled.TitleAndNameRow>
        <Controller
          control={control}
          name="personalDetails.title"
          render={({ field, fieldState }) => (
            <FinanceSelect
              label="Title*"
              options={titleOptions}
              placeholder={false}
              placeholderAsOption
              {...field}
              {...fieldState}
            />
          )}
        />
        <Controller
          control={control}
          rules={{ required: true }}
          name="personalDetails.firstName"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter first name"
              label="First name*"
            />
          )}
        />
      </Styled.TitleAndNameRow>
      <Controller
        control={control}
        name="personalDetails.surname"
        render={({ field, fieldState }) => (
          <FinanceTextField
            {...field}
            {...fieldState}
            placeholder="Enter surname"
            label="Surname*"
          />
        )}
      />
      <MobilePhoneNumberInput<Form>
        fieldName="personalDetails.phone"
        label="Mobile number*"
        placeholder="e.g. 085 123 4567"
        options={['+353']}
        phoneExtensionName="personalDetails.phoneExtension"
        isExtensionDisabled
      />
      <Controller
        control={control}
        name="personalDetails.email"
        render={({ field, fieldState }) => (
          <FinanceTextField
            {...field}
            {...fieldState}
            placeholder="Enter email"
            label="Email*"
          />
        )}
      />
    </Styled.FormInputsContainer>
  );
};

type Props = {
  displayCountryOfBirth?: boolean;
  displayMaidenName?: boolean;
  displayNumberOfDependants?: boolean;
  displayCurrentVehicleRegistrationNumber?: boolean;
  minApplicantAge?: number;
  maxApplicantAge?: number;
};

const PersonalDetails = (props: Props) => {
  const {
    displayCountryOfBirth,
    displayMaidenName,
    displayNumberOfDependants,
    displayCurrentVehicleRegistrationNumber,
    minApplicantAge = 21,
    maxApplicantAge = 70,
  } = props;

  const {
    isModalOpen: isPPSNumberInfoModalOpen,
    openModal: openPPSNumberInfoModal,
    closeModal: closePPSNumberInfoModal,
  } = useModal();

  const { control } = useFormContext<Form>();

  const title = useWatch({ control, name: 'personalDetails.title' });
  const isMaidenNameRequired = FEMALE_TITLES.includes(title);

  const currentDate = new Date();
  const minimumYearOfBirth = currentDate.getFullYear() - minApplicantAge;
  const maximumYearOfBirth = currentDate.getFullYear() - maxApplicantAge;
  const minimumMonthOfBirth = currentDate.getMonth();
  const openToDate = new Date(minimumYearOfBirth, minimumMonthOfBirth);

  return (
    <>
      <Styled.FormInputsContainer>
        <Controller
          control={control}
          name="personalDetails.dateOfBirth"
          render={({ field, fieldState }) => (
            <FinanceDatepickerField
              {...field}
              {...fieldState}
              labelContent="Date of birth*"
              placeholder="DD/MM/YYYY"
              mb="S4"
              yearStart={maximumYearOfBirth}
              yearEnd={minimumYearOfBirth}
              selected={field.value}
              value={field.value ? field.value.toDateString() : ''}
              openToDate={openToDate}
              customHeaderSelectAriaLabel="Date of birth"
            />
          )}
        />
        {displayCountryOfBirth && (
          <Controller
            control={control}
            name="personalDetails.countryOrigin"
            render={({ field, fieldState }) => (
              <FinanceSelect
                label="Country of birth*"
                options={countryOptions}
                placeholder={false}
                placeholderAsOption
                marginBottom="S4"
                {...field}
                {...fieldState}
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="personalDetails.gender"
          render={({ field, fieldState }) => (
            <FinanceSelect
              label="Gender*"
              options={genderOptions}
              placeholder={false}
              placeholderAsOption
              {...field}
              {...fieldState}
            />
          )}
        />
        <Controller
          control={control}
          name="personalDetails.maritalStatus"
          render={({ field, fieldState }) => (
            <FinanceSelect
              label="Marital status*"
              marginBottom="S4"
              options={maritalStatusOptions}
              placeholder={false}
              placeholderAsOption
              {...field}
              {...fieldState}
            />
          )}
        />
        {displayMaidenName && (
          <Controller
            control={control}
            name="personalDetails.maidenName"
            render={({ field, fieldState }) => (
              <FinanceTextField
                {...field}
                {...fieldState}
                placeholder="Enter maiden name"
                willUseSubText
                type="text"
                label={`Maiden name${isMaidenNameRequired ? '*' : ''}`}
                marginBottom="S4"
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="personalDetails.ppsNumber"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter PPS number"
              marginBottom="S4"
              label={
                <TextWithHelpIcon
                  text="PPS number*"
                  onClickIcon={openPPSNumberInfoModal}
                  aria-label="PPS number modal button"
                />
              }
            />
          )}
        />
        {displayNumberOfDependants && (
          <Controller
            control={control}
            // BE only accepts the american english version of "dependants"
            name="personalDetails.numberOfDependents"
            render={({ field, fieldState }) => (
              <FinanceSelect
                label="Number of dependants*"
                marginBottom="S4"
                options={numberOfDependantsOptions}
                placeholder={false}
                placeholderAsOption
                {...field}
                {...fieldState}
              />
            )}
          />
        )}
        {displayCurrentVehicleRegistrationNumber && (
          <Controller
            control={control}
            name="personalDetails.tradeInRegPlate"
            render={({ field, fieldState }) => (
              <FinanceTextField
                {...field}
                {...fieldState}
                marginBottom="S4"
                label="Current vehicle registration number"
                willUseSubText
                helpText="Only required if trading in"
                placeholder="Enter registration number"
                type="text"
                isOptional
              />
            )}
          />
        )}
      </Styled.FormInputsContainer>
      <DynamicPPSNumberInfoModal
        isOpen={isPPSNumberInfoModalOpen}
        onRequestClose={closePPSNumberInfoModal}
      />
    </>
  );
};

export { PersonalDetails, ContactFields };
